export default () => {
    /**
     * Format a number with thousand separators
     * @param value - The number to format
     * @param options - Optional formatting options
     * @returns Formatted number string
     */
    const formatNumber = (
        value: number | string | null | undefined,
        options: Intl.NumberFormatOptions = {},
    ): string => {
        // Handle null, undefined, or empty values
        if (value === null || value === undefined || value === "") {
            return "0";
        }

        // Convert string to number if needed
        const numValue = typeof value === "string" ? parseFloat(value) : value;

        // Handle invalid numbers
        if (isNaN(numValue)) {
            return "0";
        }

        // Default options for general number formatting
        const defaultOptions: Intl.NumberFormatOptions = {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            ...options,
        };

        // Use browser's locale for formatting
        return new Intl.NumberFormat(undefined, defaultOptions).format(
            numValue,
        );
    };

    /**
     * Format a percentage value
     * @param value - The percentage value (0-100)
     * @param decimals - Number of decimal places to show
     * @returns Formatted percentage string
     */
    const formatPercentage = (
        value: number | string | null | undefined,
        decimals = 1,
    ): string => {
        const numValue = typeof value === "string" ? parseFloat(value) : value;

        if (numValue === null || numValue === undefined || isNaN(numValue)) {
            return "0%";
        }

        return new Intl.NumberFormat(undefined, {
            style: "percent",
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(numValue / 100);
    };

    /**
     * Format a large number with compact notation (K, M, B)
     * @param value - The number to format
     * @returns Formatted compact number string
     */
    const formatCompactNumber = (
        value: number | string | null | undefined,
    ): string => {
        const numValue = typeof value === "string" ? parseFloat(value) : value;

        if (numValue === null || numValue === undefined || isNaN(numValue)) {
            return "0";
        }

        return new Intl.NumberFormat(undefined, {
            notation: "compact",
            compactDisplay: "short",
        }).format(numValue);
    };

    return {
        formatNumber,
        formatPercentage,
        formatCompactNumber,
    };
};
